import StatefulSettings, { STATE_KEY_HISTORY_POSITIONS } from "../controllers/StatefulSettings";
import {
  IPositionBasketInfo,
  PositionBasketItem,
  PositionItem,
  AuditMessage,
  IShowNotificationState,
  ISettingsItem,
} from "../ts/Definitions";
import {
  AuditActionTypes,
  ItemTypes,
  TraceLevel,
  WeekendHandling,
} from "../ts/enums";
import { NotificationType } from "../controllers/messaging";
import {
  getRandom,
  getRandomItemWithCache,
  isAvailableItem,
  getScheduleReason,
} from "../utilities/utils";
import { convertToEventDayFromDate } from "../utilities/dateUtils";
import Cookies, { COOKIE_KEY_POSITION_TICKS } from "../utilities/cookies";
import { differenceInSeconds } from "date-fns";
import Emitter from "../utilities/Emitter";
import Timer from "../utilities/Timer";
import UIFx from "uifx";
import ringSound from "../assets/ring.wav";

const ring = new UIFx(ringSound); const POSITION_TIMER_COMPLETE = "position-timer-complete";
/**
 * PositionTracker - Handles all position-related operations including:
 * - Position selection and scheduling
 * - Position timer management
 * - Position completion and rewards
 * - Position failure handling
 * - Daily position checks
 */
export class PositionTracker {
  private settings!: StatefulSettings;
  private showMessage!: (args: IShowNotificationState) => void;
  private cookies!: Cookies;
  private positionTimer!: Timer;
  private auditIncrement!: (name: string, action: string, reward: number) => void;
  private auditDecrement!: (name: string, action: string, penalty: number) => void;
  private addDailyRollPenalty!: (penalties: { reason: string; points: number }[], reason: string, points: number) => void;

  /**
   * Initialize the PositionTracker with required dependencies
   */
  public initialize(
    settings: StatefulSettings,
    showMessage: (args: IShowNotificationState) => void,
    cookies: Cookies,
    auditIncrement: (name: string, action: string, reward: number) => void,
    auditDecrement: (name: string, action: string, penalty: number) => void,
    addDailyRollPenalty: (penalties: { reason: string; points: number }[], reason: string, points: number) => void
  ): void {
    this.settings = settings;
    this.showMessage = showMessage;
    this.cookies = cookies;
    this.auditIncrement = auditIncrement;
    this.auditDecrement = auditDecrement;
    this.addDailyRollPenalty = addDailyRollPenalty;

    this.positionTimer = new Timer({
      interval: 1000,
      callbacks: [this.handlePositionTick],
    });
  }

  /**
   * Initializes position state from saved data and starts timer if needed
   */
  public initialisePosition(): void {
    const positionInfo = this.settings.getPositionInfo();
    if (positionInfo && positionInfo.endTime) {
      if (!positionInfo.endTime) positionInfo.endTime = new Date();
      const positionTimerTicks = differenceInSeconds(
        positionInfo.endTime,
        new Date()
      );
      this.settings.updatePositionState(positionInfo);
      this.cookies.setState(
        COOKIE_KEY_POSITION_TICKS,
        positionTimerTicks.toString()
      );
      this.positionTimer.start();
    } else if (this.cookies.getState(COOKIE_KEY_POSITION_TICKS)) {
      // assume the position ticks remnance and delete them
      this.cookies.clearState(COOKIE_KEY_POSITION_TICKS); // may cause a position in progress to reset in a rare case
    }
  }

  /**
   * Handles position timer ticks and completion
   */
  public handlePositionTick = (): void => {
    const now = new Date();
    const positionInfo = this.settings.getPositionInfo();
    const endTime = positionInfo.endTime;
    if (endTime && new Date(endTime) < now) {
      this.positionTimer.stop();
      delete positionInfo.endTime;
      this.cookies.clearState(COOKIE_KEY_POSITION_TICKS);
      positionInfo.expectedCount -= 1;
      Emitter.emit(
        POSITION_TIMER_COMPLETE,
        new PositionBasketItem(
          {
            ...positionInfo,
            isComplete: true,
            completedTime: new Date(),
            isPaused: false,
          } as IPositionBasketInfo,
          0
        )
      );
      if (this.settings.getIsSoundEnabled()) ring.play();
      Emitter.emitNotification({
        message: "Timed Position Complete",
        success: true,
        notificationType: NotificationType.PositionComplete,
      });
      if (positionInfo.expectedCount > 0) {
        this.auditIncrement("Position", "Partial Complete", 0);
        this.refreshPosition(positionInfo.expectedCount, positionInfo.key);
      } else {
        const positionSettings = this.settings.getPositionSettings();
        positionInfo.isComplete = true;
        positionInfo.completedTime = new Date();
        positionInfo.isPaused = false;
        const displayName = positionInfo.name;
        this.auditIncrement(
          "Position",
          "Complete",
          this.settings.incrementBank(
            this.settings.getReward(
              Math.abs(positionSettings.positionCompleteCredit)
            ),
            displayName
          )
        );
        this.settings.updatePositionState(positionInfo);
      }
    } else if (endTime) {
      // this should keep setting the cookie
      this.cookies.setState(
        COOKIE_KEY_POSITION_TICKS,
        differenceInSeconds(endTime, now).toString()
      );
    }
  };

  /**
   * Refreshes the position with a new random selection
   */
  public refreshPosition(
    currentPositionCount?: number,
    currentPosition?: string
  ): void {
    const noPositions = {
      name: "",
      isComplete: true,
      completedTime: new Date(),
      isPaused: false,
      description: "",
      hasPosition: false,
      img: "",
      itemType: ItemTypes.position,
      key: "",
      expectedCount: 0,
      initialCount: 0,
    } as IPositionBasketInfo;

    const positionSettings = this.settings.getPositionSettings();
    const handle = this.settings.canPerformSomeTasksForToday();
    const today = convertToEventDayFromDate(new Date().getDay());
    const customSchedule = this.settings.getScheduledCustomSchedule(today, new Date());
    const scheduleForcesPositions = customSchedule?.positionsEnabled === true;
    const scheduleDisablesPositions = customSchedule?.positionsEnabled === false;

    let positions = this.settings.getPositions((item) => isAvailableItem(item));
    positions = positions.filter((p) =>
      this.settings.itemAvailableForDay(
        p,
        today,
        false,
        false
      )
    );

    if (
      !handle ||
      scheduleDisablesPositions ||
      (!scheduleForcesPositions && positionSettings.disablePositions) ||
      positions.length === 0
    ) {
      this.settings.updatePositionState(noPositions);
      return;
    }

    const configuredCount = customSchedule?.positionCount;
    let expectedCount =
      currentPositionCount ??
      configuredCount ??
      getRandom(
        positionSettings.minPositionCount,
        positionSettings.maxPositionCount
      );
    const existingInfo = this.settings.getPositionInfo();

    const generalSettings = this.settings.getGeneralSettings();
    if (
      this.settings.getIsWeekend() &&
      currentPositionCount === undefined &&
      configuredCount === undefined
    ) {
      if (generalSettings.weekendHandling === WeekendHandling.harsh)
        expectedCount += expectedCount;
      if (generalSettings.weekendHandling === WeekendHandling.easy)
        expectedCount = Math.round(expectedCount / 2);
    }
    const positionsToSelectFrom = [...positions];
    if (positionsToSelectFrom.length > 1 && currentPosition) {
      const idx = positionsToSelectFrom.findIndex(
        (p) => p.key === currentPosition
      );
      if (idx > -1) positionsToSelectFrom.splice(idx, 1);
    }

    const position =
      expectedCount <= 0
        ? undefined
        : getRandomItemWithCache(
          positionsToSelectFrom,
          STATE_KEY_HISTORY_POSITIONS,
          (p) => p.key,
          this.settings,
          true
        );

    if (!position) {
      this.settings.updatePositionState(noPositions);
      return;
    }
    this.addPositionToSchedule(position, expectedCount, existingInfo);
  }

  /**
   * Adds a position to the schedule
   */
  public addPositionToSchedule(
    position: PositionItem,
    expectedCount: number,
    existingInfo?: IPositionBasketInfo
  ): void {
    const positionInfo = {
      ...position,
      hasPosition: true,
      isComplete: false,
      isPaused: true,
      expectedCount,
      initialCount:
        (existingInfo && existingInfo.initialCount) || expectedCount,
      scheduleReason: getScheduleReason(position),
    } as IPositionBasketInfo;
    this.settings.updatePositionState(positionInfo);
  }

  /**
   * Handles position failure
   */
  public handlePositionFailed(): { penalty: number } {
    const positionInfo = this.settings.getPositionInfo();
    if (positionInfo && positionInfo.endTime) {
      delete positionInfo.endTime;
      this.positionTimer.stop();
      this.cookies.clearState(COOKIE_KEY_POSITION_TICKS);
      positionInfo.isPaused = true;
      this.settings.updatePositionState(positionInfo);
    }
    const positionSettings = this.settings.getPositionSettings();
    const penalty = positionSettings.positionCompleteCredit * 2;
    return { penalty };
  }

  /**
   * Checks position completion for daily roll
   */
  public checkPositionCompletion(
    penalties: { reason: string; points: number }[]
  ): number {
    let bankCharge = 0;
    const positionInfo = this.settings.getPositionInfo();
    const positionSettings = this.settings.getPositionSettings();
    const didDoPosition = positionInfo.isComplete;
    if (
      positionSettings.disablePositions ||
      !positionInfo.hasPosition ||
      !isAvailableItem(
        this.settings.getPosition(positionInfo.key) as ISettingsItem
      )
    )
      return bankCharge;
    if (
      !didDoPosition &&
      (positionInfo.isPaused ||
        positionInfo.initialCount + 1 !== positionInfo.expectedCount)
    ) {
      // either it is paused so nothing in progress or the one is still being done but it is not enough
      const positionCharge = positionSettings.positionCompleteCredit * 2;
      bankCharge += positionCharge;
      this.auditDecrement("Position Missed", "Daily Checks", positionCharge);
      this.showMessage({
        message: "Penalty applied for failing to perform positions",
        error: true,
        notificationType: NotificationType.ErrorInfo,
      });
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Penalty,
          "Unfinished Positions",
          "Uncompleted Penalty Accrued",
          TraceLevel.warning
        )
      );
      this.addDailyRollPenalty(penalties, "Unfinished Positions", 1);
    }
    return bankCharge;
  }

  /**
   * Starts the position timer
   */
  public startPositionTimer(): void {
    this.positionTimer.start();
  }

  /**
   * Stops the position timer
   */
  public stopPositionTimer(): void {
    this.positionTimer.stop();
  }

  /**
   * Cleanup on disposal
   */
  public dispose(): void {
    this.positionTimer?.stop();
  }
}
